//#region IMPORT
import React from 'react';
import { Unstable_Grid2 as Grid, Box, Tabs, Tab } from '@mui/material';
import { AuthButton } from '../../../../Components/MUIs';
import { PropTypes } from 'prop-types';
import dayjs from 'dayjs';
import ACS_BASIC_01F from '../ACS_BASIC_01/ACS_BASIC_01F';
import ACS_BASIC_03F from '../ACS_BASIC_03/ACS_BASIC_03F';
//#endregion

function A11yProps(index) {
  return {
    id: `acs-basic-tab-${index}`,
    'aria-controls': `acs-basic-tabpanel-${index}`,
  };
}

function CustomTabPanel(props) {
  const { children, value, index } = props;
  return (
    <React.Fragment>
      {value === index && (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          {children}
        </Box>
      )}
    </React.Fragment>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function ACS_BASIC(props) {
  const searchRef = React.useRef({});

  React.useEffect(() => {
    if (props.searchInfo) {
      searchRef.current = {
        custCd: props.searchInfo.custCd || '',
        depCd: props.searchInfo.depCd || '',
        startDate: props.searchInfo.startDate || '',
        endDate: props.searchInfo.endDate || '',
        year: props.searchInfo.year || '',
        month: props.searchInfo.month || '',
        gubun: props.searchInfo.gubun || '',
        searchText: props.searchInfo.searchText || '',
      };
    }
  }, [props.searchInfo]);

  // 탭0(산업재해현황): 회사, 사업장, 시작일시, 종료일시
  const conditionsTab0 = [
    ...(props.userInfo?.authCd === 'ADMIN'
      ? [
          {
            type: 'combo',
            name: '회사',
            items: props.searchInfo?.coList ?? [],
            value: props.searchInfo?.coCd ?? '',
            onChange: (e) => {
              props.handleCoChange?.(e);
              if (props.validateSelection?.()) props.setSearchClick1?.(true);
            },
          },
        ]
      : []),
    {
      type: 'combo',
      name: '사업장',
      items: props.searchInfo?.custList ?? [],
      value: props.searchInfo?.custCd ?? '',
      onChange: (e) => {
        props.handleCustChange?.(e);
        if (props.validateSelection?.()) props.setSearchClick1?.(true);
      },
    },
    {
      type: 'datePicker',
      name: '시작일시',
      value: props.startDate ? dayjs(props.startDate) : dayjs(),
      onChange: (date) => {
        props.handleStartDateChange?.(date);
        if (props.validateSelection?.()) props.setSearchClick1?.(true);
      },
      currentLocale: 'ko',
    },
    {
      type: 'datePicker',
      name: '종료일시',
      value: props.endDate ? dayjs(props.endDate) : dayjs(),
      onChange: (date) => {
        props.handleEndDateChange?.(date);
        if (props.validateSelection?.()) props.setSearchClick1?.(true);
      },
      currentLocale: 'ko',
    },
  ];

  // 탭1(아차사고현황): 회사, 사업장, 부서, 년월, 상태, 검색어(신청인, 작성인)
  const conditionsTab1 = [
    ...(props.userInfo?.authCd === 'ADMIN'
      ? [
          {
            type: 'combo',
            name: '회사',
            items: props.searchInfo?.coList ?? [],
            value: props.searchInfo?.coCd ?? '',
            onChange: (e) => {
              props.handleCoChange?.(e);
              if (props.validateSelection?.()) props.setSearchClick2?.(true);
            },
          },
        ]
      : []),
    {
      type: 'combo',
      name: '사업장',
      items: props.searchInfo?.custList ?? [],
      value: props.searchInfo?.custCd ?? '',
      onChange: (e) => {
        props.handleCustChange?.(e);
        if (props.validateSelection?.()) props.setSearchClick2?.(true);
      },
    },
    {
      type: 'combo',
      name: '부서',
      items: [
        { value: '', name: '전체' },
        ...(props.searchInfo?.depList ?? []),
      ],
      value: props.searchInfo?.depCd ?? '',
      onChange: async (e) => {
        await props.handleDepChange?.(e);

        if (props.value === 0) {
          await props.setSearchClick1?.(true);
        } else if (props.value === 1) {
          await props.setSearchClick2?.(true);
        } else if (props.value === 2) {
          await props.setSearchClick3?.(true);
        }
      },
    },
    {
      type: 'combo',
      name: '공정',
      items: props.gongjongList ?? [],
      value: props.gongjongCd ?? '',
      onChange: (value) => {
        props.handleGongjongChange?.(value);
      },
    },
    {
      type: 'datePicker',
      name: '년월',
      views: ['year', 'month'],
      openTo: 'month',
      // 검색조건 년월은 연-월까지만 사용 (일자는 의미 없음)
      value: dayjs(
        `${props.searchInfo?.year || dayjs().format('YYYY')}-${props.searchInfo?.month || dayjs().format('MM')}`,
        'YYYY-MM'
      ),
      onChange: (date) => {
        props.handleYearMonthChange?.(date);
        if (props.validateSelection?.()) props.setSearchClick2?.(true);
      },
      format: 'YYYY-MM',
      currentLocale: 'ko',
    },
    {
      type: 'combo',
      name: '상태',
      items: [
        { value: '', name: '전체' },
        { value: '0', name: '확인 전' },
        { value: '1', name: '완료' },
      ],
      value: props.searchInfo?.gubun ?? '',
      onChange: (value) => {
        props.dispatch?.({
          type: 'SearchInfo/setGubun',
          payload: value ?? '',
        });
        if (props.validateSelection?.()) props.setSearchClick2?.(true);
      },
    },
  ];

  // 탭2(통계): 회사, 사업장, 조회년도
  const conditionsTab2 = [
    ...(props.userInfo?.authCd === 'ADMIN'
      ? [
          {
            type: 'combo',
            name: '회사',
            items: props.searchInfo?.coList ?? [],
            value: props.searchInfo?.coCd ?? '',
            onChange: (e) => {
              props.handleCoChange?.(e);
              if (props.validateSelection?.()) props.setSearchClick3?.(true);
            },
          },
        ]
      : []),
    {
      type: 'combo',
      name: '사업장',
      items: props.searchInfo?.custList ?? [],
      value: props.searchInfo?.custCd ?? '',
      onChange: (e) => {
        props.handleCustChange?.(e);
        if (props.validateSelection?.()) props.setSearchClick3?.(true);
      },
    },
    {
      type: 'datePicker',
      name: '조회연도',
      views: ['year'],
      openTo: 'year',
      value: props.searchYear ? dayjs(props.searchYear, 'YYYY') : dayjs(),
      onChange: (date) => {
        props.handleYearChange?.(date);
        if (props.validateSelection?.()) props.setSearchClick3?.(true);
      },
      currentLocale: 'ko',
      format: 'YYYY',
    },
  ];

  const conditions =
    props.value === 0
      ? conditionsTab0
      : props.value === 1
        ? conditionsTab1
        : conditionsTab2;

  return (
    <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12 }}>
      <Grid xs={4} md={12}>
        <AuthButton
          menuId="Accidents"
          addYn={props.value === 0 ? 'Y' : 'N'}
          searchBox={props.value === 2 ? 'N' : 'Y'}
          saveYn={props.value === 2 ? 'N' : 'Y'}
          printYn="N"
          placeholder={
            props.value === 2
              ? undefined
              : props.value === 1
                ? '검색어(신청인,작성인)'
                : '검색어(성명)'
          }
          searchClick={(searchTextFromBox) => {
            if (!props.validateSelection?.()) return;
            if (typeof searchTextFromBox === 'string')
              props.setSearchText?.(searchTextFromBox);
            if (props.value === 0) props.setSearchClick1?.(true);
            else if (props.value === 1) props.setSearchClick2?.(true);
          }}
          saveClick={() => {
            if (!props.validateSelection?.()) return;
            if (props.value === 0) props.setSaveClick1?.(true);
            else if (props.value === 1) props.setSaveClick2?.(true);
            else if (props.value === 2) props.setSaveClick3?.(true);
          }}
          deleteClick={() => {
            if (!props.validateSelection?.()) return;
            if (props.value === 0) props.setDeleteClick1?.(true);
            else if (props.value === 1) props.setDeleteClick2?.(true);
            else if (props.value === 2) props.setDeleteClick3?.(true);
          }}
          printClick={() => {
            if (!props.validateSelection?.()) return;
            if (props.value === 0) props.setPrintClick1?.(true);
            else if (props.value === 1) props.setPrintClick2?.(true);
            else if (props.value === 2) props.setPrintClick3?.(true);
          }}
          conditions={conditions}
          grids={
            <React.Fragment>
              <Box>
                <Tabs
                  value={props.value === 2 ? 1 : props.value}
                  onChange={(e, newVal) =>
                    props.HandleChange?.(e, newVal === 1 ? 2 : newVal)
                  }
                  aria-label="재해현황 탭"
                >
                  <Tab
                    label="산업재해현황"
                    {...A11yProps(0)}
                    sx={{
                      fontSize: '0.95rem',
                      fontWeight: 'bold',
                      color: '#000',
                    }}
                  />
                  <Tab
                    label="통계"
                    {...A11yProps(1)}
                    sx={{
                      fontSize: '0.95rem',
                      fontWeight: 'bold',
                      color: '#000',
                    }}
                  />
                </Tabs>
              </Box>
              <CustomTabPanel
                value={props.value === 2 ? 1 : props.value}
                index={0}
              >
                <ACS_BASIC_01F
                  searchInfo={props.searchInfo}
                  filterClick1={props.filterClick1}
                  setFilterClick1={props.setFilterClick1}
                  searchClick1={props.searchClick1}
                  setSearchClick1={props.setSearchClick1}
                  saveClick1={props.saveClick1}
                  setSaveClick1={props.setSaveClick1}
                  deleteClick1={props.deleteClick1}
                  setDeleteClick1={props.setDeleteClick1}
                  printClick1={props.printClick1}
                  setPrintClick1={props.setPrintClick1}
                  validateSelection={props.validateSelection}
                />
              </CustomTabPanel>
              <CustomTabPanel
                value={props.value === 2 ? 1 : props.value}
                index={1}
              >
                <ACS_BASIC_03F
                  searchInfo={props.searchInfo}
                  filterClick3={props.filterClick3}
                  setFilterClick3={props.setFilterClick3}
                  searchClick3={props.searchClick3}
                  setSearchClick3={props.setSearchClick3}
                  saveClick3={props.saveClick3}
                  setSaveClick3={props.setSaveClick3}
                  deleteClick3={props.deleteClick3}
                  setDeleteClick3={props.setDeleteClick3}
                  printClick3={props.printClick3}
                  setPrintClick3={props.setPrintClick3}
                  validateSelection={props.validateSelection}
                />
              </CustomTabPanel>
            </React.Fragment>
          }
        />
      </Grid>
    </Grid>
  );
}

export default ACS_BASIC;
